/**
 * Affichage des valeurs numériques du socle de listes (UPT-2111 Q-03, Q-04).
 *
 * Un montant, un nombre, un pourcentage ou une date doit (1) se lire en chiffres de largeur fixe pour que les
 * colonnes s'alignent sur leurs unités (`tabular-nums`), (2) se coller à la fin de sa colonne (`text-end`, jamais
 * `text-right` : le sens suit la langue), (3) ne jamais se couper (`whitespace-nowrap`) et (4) rester ISOLÉ de la
 * phrase qui l'entoure : dans une interface arabe (RTL), « 1 240 000,00 € » sans isolation s'affiche
 * « € 000,00 240 1 ».
 *
 * Ce module ne DEVINE rien sur le nom d'un champ. Le type vient de ce que l'appelant sait déjà :
 *   - le type du `field_schema` (`amount`, `number`, `date`, `datetime`, `time`) ;
 *   - le type du champ de dictionnaire (`numberField`, `dateField`, `dateTimeField`) ;
 *   - la colonne AG-Grid elle-même (`valueKind` posé ici — extension Géode HORS du ColDef d'AG Grid 24, voir
 *     `NumericColumnMeta` —, `type: numericColumn`, filtre `agNumberColumnFilter` / `agDateColumnFilter`) ;
 *     relecture du 18.10 (W14) : `cellDataType` n'existe pas en AG Grid 24, il n'est plus lu.
 *   - le `format` d'une fiche de carte (`currency`, `number`, `percent`, `date`, `datetime`).
 */

export type NumericKind = 'amount' | 'number' | 'percent' | 'date' | 'datetime' | 'time';

type ColDef = Record<string, unknown>;

/**
 * Nature numérique d'une colonne : extension Géode posée À CÔTÉ du ColDef d'AG Grid (qui ne la connaît pas), relue par
 * les cartes du socle de listes. Jamais lue par la grille elle-même.
 */
export interface NumericColumnMeta {
  valueKind?: NumericKind;
}

/** Valeur rendue dans du JSX : classes de la balise `<bdi>` de `NumericValue`. */
export const NUMERIC_VALUE_CLASSES = 'tabular-nums whitespace-nowrap';

/**
 * Cellule de grille : la valeur est une CHAÎNE posée par AG-Grid, sans balise à nous. L'isolation passe donc par la
 * classe `ui-bidi-isolate` (design system : `unicode-bidi: isolate` + `direction: ltr`). L'alignement de fin est
 * assuré par `type: 'rightAligned'` (AG-Grid le retourne en RTL, y compris pour l'en-tête) ; `text-end` double la
 * règle pour les cellules rendues hors du thème AG-Grid.
 */
export const NUMERIC_CELL_CLASSES = 'tabular-nums whitespace-nowrap text-end ui-bidi-isolate';

const NUMERIC_KINDS: ReadonlySet<string> = new Set(['amount', 'number', 'percent', 'date', 'datetime', 'time']);

const SCHEMA_TYPE_KINDS: Record<string, NumericKind> = {
  amount: 'amount',
  number: 'number',
  date: 'date',
  datetime: 'datetime',
  time: 'time',
};

const DICTIONARY_FIELD_TYPE_KINDS: Record<string, NumericKind> = {
  numberField: 'number',
  dateField: 'date',
  dateTimeField: 'datetime',
};

const GRID_FILTER_KINDS: Record<string, NumericKind> = {
  agNumberColumnFilter: 'number',
  agDateColumnFilter: 'date',
};

const CARD_FORMAT_KINDS: Record<string, NumericKind> = {
  currency: 'amount',
  number: 'number',
  percent: 'percent',
  date: 'date',
  datetime: 'datetime',
};

function lookup(table: Record<string, NumericKind>, key: unknown): NumericKind | null {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(table, key) ? table[key] : null;
}

const DIGIT_RE = /[0-9]/;
// Hébreu, arabe, syriaque, thaana, n'ko, samaritain, mandéen + formes de présentation arabes : écritures de droite à gauche.
const RTL_LETTER_RE = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;

/**
 * Une VALEUR (nombre, ou texte déjà formaté) qui se lit comme une valeur numérique : elle contient un chiffre et
 * aucune lettre d'une écriture de droite à gauche. Sert aux composants génériques (StatCard…) qui reçoivent un
 * `ReactNode` sans connaître le type du champ : « 5.2M » ou « 1 240 000,00 € » sont isolés, « Actif » ou une
 * valeur en arabe ne le sont pas (forcer la gauche-droite sur du texte arabe le casserait).
 */
export function isIsolatableNumericValue(value: unknown): value is string | number {
  if (typeof value === 'number') return Number.isFinite(value);
  return typeof value === 'string' && DIGIT_RE.test(value) && !RTL_LETTER_RE.test(value);
}

/** Type d'un élément de `field_schema` (`DynamicFieldType`). */
export function numericKindFromSchemaType(type: unknown): NumericKind | null {
  return lookup(SCHEMA_TYPE_KINDS, typeof type === 'string' ? type.toLowerCase() : type);
}

/**
 * Champ de dictionnaire (`getField(...)`) : `savedFieldType: 'numberField'` marque les montants, dont l'affichage
 * est un champ texte (`fieldType: 'textField'`) ; `fieldType` porte le reste.
 */
export function numericKindFromDictionaryField(field: unknown): NumericKind | null {
  if (!field || typeof field !== 'object') return null;
  const { savedFieldType, fieldType } = field as { savedFieldType?: unknown; fieldType?: unknown };
  return lookup(DICTIONARY_FIELD_TYPE_KINDS, savedFieldType) ?? lookup(DICTIONARY_FIELD_TYPE_KINDS, fieldType);
}

/** Fiche de carte (`EntityCardFieldDef.format`). */
export function numericKindFromCardFormat(format: unknown): NumericKind | null {
  return lookup(CARD_FORMAT_KINDS, format);
}

/** Colonne AG-Grid : son `valueKind` (NumericColumnMeta), puis les indications de type qu'elle porte (filtre typé, `numericColumn`). */
export function numericKindFromColDef(colDef: unknown): NumericKind | null {
  if (!colDef || typeof colDef !== 'object') return null;
  const col = colDef as NumericColumnMeta & { filter?: unknown; type?: unknown };
  if (typeof col.valueKind === 'string' && NUMERIC_KINDS.has(col.valueKind)) return col.valueKind;
  const fromFilter = lookup(GRID_FILTER_KINDS, col.filter);
  if (fromFilter) return fromFilter;
  const types = Array.isArray(col.type) ? col.type : [col.type];
  return types.includes('numericColumn') ? 'number' : null;
}

function mergeClasses(existing: unknown, additions: string): unknown {
  const wanted = additions.split(/\s+/).filter(Boolean);
  if (existing == null || existing === '') return additions;
  if (typeof existing === 'string') {
    const present = new Set(existing.split(/\s+/));
    const missing = wanted.filter((name) => !present.has(name));
    return missing.length === 0 ? existing : `${existing} ${missing.join(' ')}`;
  }
  if (Array.isArray(existing)) {
    const missing = wanted.filter((name) => !existing.includes(name));
    return missing.length === 0 ? existing : [...existing, ...missing];
  }
  if (typeof existing === 'function') {
    // Garde le `cellClass` fonction de l'appelant et lui ajoute nos classes.
    const original = existing as (...args: unknown[]) => unknown;
    return (...args: unknown[]) => mergeClasses(original(...args), additions);
  }
  return existing;
}

function mergeColumnType(existing: unknown, addition: string): unknown {
  if (existing == null) return addition;
  const list = Array.isArray(existing) ? existing : [existing];
  return list.includes(addition) ? existing : [...list, addition];
}

/**
 * Pose sur une colonne AG-Grid ce qu'il faut à une valeur numérique : `valueKind` (relu par les cartes), alignement
 * de fin (colonne et en-tête), chiffres tabulaires, pas de coupure, isolation bidi.
 * Une colonne qui porte déjà un rendu de cellule (badge de statut, barre de progression, valeur caviardée…) n'est
 * pas touchée : son rendu décide de sa mise en page.
 */
export function applyNumericCell<T extends ColDef>(colDef: T, kind: NumericKind | null): T & NumericColumnMeta {
  if (!kind || colDef.cellRenderer != null) return colDef;
  return {
    ...colDef,
    valueKind: kind,
    type: mergeColumnType(colDef.type, 'rightAligned'),
    cellClass: mergeClasses(colDef.cellClass, NUMERIC_CELL_CLASSES),
  };
}
